import React, { useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Check, Pencil, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { DatePicker } from '@/components/ui/date-picker';
import { Field, FieldError, FieldLabel } from '@/components/ui/field';
import { TimePicker } from '@/components/ui/time-picker';
import { useTaskFieldEdit } from './TaskFieldEditContext';
import type { TaskPencilFieldKey } from './taskFieldEditModel';

type TicketDateTimeFieldProps = {
  title: string;
  fieldName: TaskPencilFieldKey;
  disabled?: boolean;
};

function asDate(value: unknown): Date | undefined {
  if (!value) return undefined;
  const date = value instanceof Date ? value : new Date(String(value));
  return Number.isNaN(date.getTime()) ? undefined : date;
}

function timeFrom(date: Date | undefined): string {
  if (!date) return '';
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
}

function combine(date: Date | undefined, time: string): Date | null {
  if (!date) return null;
  const next = new Date(date);
  const [hours, minutes] = (time || '00:00').split(':').map(Number);
  next.setHours(hours || 0, minutes || 0, 0, 0);
  return next;
}

function formatStored(value: unknown): string {
  const date = asDate(value);
  if (!date) return '—';
  return `${date.toLocaleDateString()} ${timeFrom(date)}`;
}

export const TicketDateTimeField: React.FC<TicketDateTimeFieldProps> = ({
  title,
  fieldName,
  disabled,
}) => {
  const {
    control,
    getValues,
    setValue,
    formState: { errors },
  } = useFormContext();
  const { autoPersist, persistTip, editingFieldKey, beginEdit, endEdit } =
    useTaskFieldEdit();
  const error = errors[fieldName]?.message as string | undefined;
  const dateId = `${fieldName}-date`;
  const timeId = `${fieldName}-time`;
  const isEditing = autoPersist && editingFieldKey === fieldName;
  const [draft, setDraft] = useState<Date | null>(() =>
    asDate(getValues(fieldName)) ?? null
  );

  useEffect(() => {
    if (isEditing) {
      setDraft(asDate(getValues(fieldName)) ?? null);
    }
  }, [fieldName, getValues, isEditing]);

  if (!autoPersist) {
    return (
      <Field data-invalid={error ? true : undefined}>
        <FieldLabel htmlFor={dateId}>{title}</FieldLabel>
        <Controller
          control={control}
          name={fieldName}
          render={({ field }) => {
            const date = asDate(field.value);
            return (
              <div className="tw:grid tw:grid-cols-[minmax(0,1fr)_auto] tw:gap-2">
                <DatePicker
                  id={dateId}
                  value={date}
                  disabled={disabled}
                  aria-label={`${title} date`}
                  placeholder="Pick a date"
                  onChange={(next) =>
                    field.onChange(
                      combine(next, timeFrom(date) || timeFrom(next))
                    )
                  }
                />
                <TimePicker
                  id={timeId}
                  value={timeFrom(date)}
                  disabled={disabled || !date}
                  aria-label={`${title} time`}
                  className="tw:min-w-[7.5rem]"
                  onChange={(next) => field.onChange(combine(date, next))}
                />
              </div>
            );
          }}
        />
        <FieldError>{error}</FieldError>
      </Field>
    );
  }

  const commit = () => {
    setValue(fieldName, draft, { shouldDirty: true, shouldValidate: true });
    endEdit();
    void persistTip();
  };

  const cancel = () => {
    setDraft(asDate(getValues(fieldName)) ?? null);
    endEdit();
  };

  return (
    <Field data-invalid={error ? true : undefined}>
      <div className="tw:flex tw:items-center tw:justify-between tw:gap-2">
        <FieldLabel htmlFor={dateId}>{title}</FieldLabel>
        {disabled ? null : isEditing ? (
          <div className="tw:flex tw:gap-1">
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              aria-label={`Save ${title}`}
              onClick={commit}
            >
              <Check className="tw:size-4" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              aria-label={`Cancel ${title}`}
              onClick={cancel}
            >
              <X className="tw:size-4" />
            </Button>
          </div>
        ) : (
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label={`Edit ${title}`}
            onClick={() => beginEdit(fieldName)}
          >
            <Pencil className="tw:size-4" />
          </Button>
        )}
      </div>
      {isEditing ? (
        <div className="tw:grid tw:grid-cols-[minmax(0,1fr)_auto] tw:gap-2">
          <DatePicker
            id={dateId}
            value={draft ?? undefined}
            disabled={disabled}
            aria-label={`${title} date`}
            placeholder="Pick a date"
            onChange={(next) =>
              setDraft(combine(next, timeFrom(draft ?? undefined) || timeFrom(next)))
            }
          />
          <TimePicker
            id={timeId}
            value={timeFrom(draft ?? undefined)}
            disabled={disabled || !draft}
            aria-label={`${title} time`}
            className="tw:min-w-[7.5rem]"
            onChange={(next) => setDraft(combine(draft ?? undefined, next))}
          />
        </div>
      ) : (
        <p className="tw:m-0 tw:text-sm">{formatStored(getValues(fieldName))}</p>
      )}
      <FieldError>{error}</FieldError>
    </Field>
  );
};
